import React from "react";
import { Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const CartItem = ({ item, updateCartPrice, updateProductSize, updateProductPharmacy, deleteCartItem, readOnly, isError }) => {
    // Compact readonly view for Summary & Payment section
    if (readOnly) {
        return (
            <div className="flex gap-3 items-start">
                {/* Small Product Image */}
                <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
                    <img 
                        src={item.ThumbnailURL} 
                        alt={item.ProductName}
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Compact Product Details */}
                <div className="flex-1 space-y-1">
                    <h4 className="font-semibold text-sm leading-tight">
                        {item.ProductName}
                    </h4>
                    
                    {/* Price, Size & Pharmacy in single row */}
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="font-semibold text-accent">${item.selectedPrice?.QuantityPrice}</span>
                        {item.selectedSize?.Size && (
                            <>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Size: {item.selectedSize.Size}</span>
                            </>
                        )}
                        {item.selectedPharmacy?.Name && (
                            <>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">{item.selectedPharmacy.Name}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Full editable view for cart
    return (
        <Card className={cn("overflow-hidden", isError && "border-destructive border-2")}>
            <CardContent className="p-4">
                <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
                        <img 
                            src={item.ThumbnailURL} 
                            alt={item.ProductName}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 space-y-3">
                        {/* Product Name and Delete */}
                        <div className="flex items-start justify-between">
                            <h4 className="font-semibold text-md leading-tight pr-2">
                                {item.ProductName}
                            </h4>
                            {!readOnly && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => deleteCartItem(item.ProductID)}
                                    className="h-6 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>

                        {/* Price Select */}
                        {item.Prices && item.Prices.length > 0 && (
                            <div className="space-y-1.5 max-w-[140px]">
                                <Label className="text-xs text-muted-foreground">Price</Label>
                                <Select 
                                    value={item.selectedPrice?.QuantityThreshold}
                                    onValueChange={(value) => {
                                        const selected = item.Prices.find(p => p.QuantityThreshold === value);
                                        updateCartPrice(item.ProductID, selected);
                                    }}
                                    disabled={readOnly}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue>
                                            <span className="flex items-center gap-1.5">
                                                <span className="font-semibold">${item.selectedPrice?.QuantityPrice}</span>
                                                <span className="text-muted-foreground">-</span>
                                                <span className="text-sm">{item.selectedPrice?.QuantityThreshold}</span>
                                            </span>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {item.Prices.map((price, index) => (
                                            <SelectItem key={index} value={price.QuantityThreshold}>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">${price.QuantityPrice}</span>
                                                    <span className="text-muted-foreground">-</span>
                                                    <span>{price.QuantityThreshold}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Size Radio Buttons (like reference image) */}
                        {item.Sizes && item.Sizes.length > 0 && (
                            <div className="space-y-2">
                                <Label className={cn("text-xs", isError && !item.selectedSize && "text-destructive")}>
                                    Size {!item.selectedSize && "*"}
                                </Label>
                                <RadioGroup
                                    value={item.selectedSize?.Size}
                                    onValueChange={(value) => {
                                        const selected = item.Sizes.find(s => s.Size === value);
                                        updateProductSize(item.ProductID, selected);
                                    }}
                                    disabled={readOnly}
                                    className="flex flex-wrap gap-2"
                                >
                                    {item.Sizes.map((size, index) => (
                                        <div key={index}>
                                            <RadioGroupItem
                                                value={size.Size}
                                                id={`size-${item.ProductID}-${size.Size}`}
                                                className="peer sr-only"
                                            />
                                            <Label
                                                htmlFor={`size-${item.ProductID}-${size.Size}`}
                                                className={cn(
                                                    "flex items-center justify-center px-3 py-1.5 text-sm border rounded-md cursor-pointer",
                                                    "peer-data-[state=checked]:bg-accent peer-data-[state=checked]:text-white peer-data-[state=checked]:border-accent",
                                                    "hover:bg-gray-50",
                                                    readOnly && "cursor-not-allowed opacity-50"
                                                )}
                                            >
                                                {size.Size}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}

                        {/* Pharmacy Radio Buttons */}
                        {item.Pharmacies && item.Pharmacies.length > 0 && (
                            <div className="space-y-2">
                                <Label className={cn("text-xs", isError && !item.selectedPharmacy && "text-destructive")}>
                                    Pharmacy {!item.selectedPharmacy && "*"}
                                </Label>
                                <RadioGroup
                                    value={item.selectedPharmacy?.Name}
                                    onValueChange={(value) => {
                                        const selected = item.Pharmacies.find(p => p.Name === value);
                                        updateProductPharmacy(item.ProductID, selected);
                                    }}
                                    disabled={readOnly}
                                    className="flex flex-wrap gap-2"
                                >
                                    {item.Pharmacies.map((pharmacy, index) => (
                                        <div key={index}>
                                            <RadioGroupItem
                                                value={pharmacy.Name}
                                                id={`pharmacy-${item.ProductID}-${pharmacy.Name}`}
                                                className="peer sr-only"
                                            />
                                            <Label
                                                htmlFor={`pharmacy-${item.ProductID}-${pharmacy.Name}`}
                                                className={cn(
                                                    "flex items-center justify-center px-3 py-1.5 text-sm border rounded-md cursor-pointer",
                                                    "peer-data-[state=checked]:bg-accent peer-data-[state=checked]:text-white peer-data-[state=checked]:border-accent",
                                                    "hover:bg-gray-50",
                                                    readOnly && "cursor-not-allowed opacity-50"
                                                )}
                                            >
                                                {pharmacy.Name}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default CartItem;

